import React, { useCallback, useEffect, useRef, useState } from "react";

import type { PointData } from "pixi.js";
import { Container, Graphics, Text } from "pixi.js";
import { extend, useTick } from "@pixi/react";

extend({ Text });
extend({ Graphics });
extend({ Container });

interface PixBimTooltipProps {
  texts?: string[];
  textColor?: string;
  fontSize?: number;
  position: PointData;
}

const PixBimTooltip: React.FC<PixBimTooltipProps> = ({
  texts,
  position,
  fontSize = 12,
  textColor = "#000000",
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [dimension, setDimension] = useState<{ width: number; height: number }>({
    width: 100,
    height: 100,
  });
  const toolTipRef = useRef<Container>(null);
  const textRef = useRef<Text>(null);
  useEffect(() => {
    setIsVisible(true);
    const timeoutId = setTimeout(() => {
      setIsVisible(false);
    }, 1500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [texts]);
  useTick(() => {
    if (toolTipRef.current && isVisible) {
      toolTipRef.current.position = {
        x: position.x + 20,
        y: position.y - 30,
      };
    }
    if (isVisible) {
      if (textRef.current?.height !== dimension.height) {
        setDimension({
          width: textRef.current?.width || 100,
          height: textRef.current?.height || 100,
        });
      }
    }
  });
  const draw = useCallback(
    (g: Graphics) => {
      const { width, height } = dimension;
      g.clear();
      g.drawRect(-5, -5, width + 10, height + 10);
      g.eventMode = "static";
      g.stroke({ color: "#000000" });
      g.fill({ color: "#ffffff" });
    },
    [dimension]
  );
  if (!isVisible) {
    return null;
  }
  return (
    <container ref={toolTipRef}>
      <pixiText
        ref={textRef}
        text={texts?.join("\n")}
        style={{ fontSize, align: "center", fill: textColor, wordWrap: true, wordWrapWidth: 100 }}
        zIndex={1}
      />
      <graphics draw={draw} />
    </container>
  );
};

export default PixBimTooltip;
